// ---------------------------------------------------------------------------
// footer.content.ts — Translated content for Footer component
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface FooterContent {
  brand: string;
  columns: {
    platform: string;
    solutions: string;
    company: string;
    legalSupport: string;
    byPropertyType: string;
    compare: string;
    integrations: string;
    byCity: string;
    compliance: string;
  };
  links: {
    product: string[];
    solutions: string[];
    company: string[];
    support: string[];
    propertyTypes: string[];
    cities: string[];
    compliance: string[];
  };
  badges: {
    certified: string;
    level1: string;
    compliant: string;
    uptime: string;
  };
  bottom: {
    rights: string;
    privacy: string;
    terms: string;
    security: string;
    refundPolicy: string;
  };
}

const en: FooterContent = {
  brand: 'Enterprise-grade hotel management platform. Streamline bookings, operations, and guest experiences with one unified system.',
  columns: {
    platform: 'Platform',
    solutions: 'Solutions',
    company: 'Company',
    legalSupport: 'Legal & Support',
    byPropertyType: 'By Property Type',
    compare: 'Compare',
    integrations: 'Integrations',
    byCity: 'By City',
    compliance: 'Compliance',
  },
  links: {
    product: ['Features', 'Pricing', 'Partner Program', 'Why HotelStack.io', 'Resilience'],
    solutions: ['Hotel Management', 'Hotel PMS', 'Channel Manager', 'Booking Engine', 'Guest CRM', 'Revenue AI'],
    company: ['About Us', 'Contact', 'Blog', 'Register Hotel'],
    support: ['Partner Program', 'BookMyRoom.io'],
    propertyTypes: ['Small Hotels', 'Boutique Hotels', 'Resorts', 'Hotel Chains', 'Hostels'],
    cities: ['Dubai', 'Bangkok', 'London', 'New York', 'Singapore', 'Paris', 'Tokyo', 'Istanbul', 'Sydney', 'Los Angeles'],
    compliance: ['EU & UK', 'United States', 'UAE & Middle East', 'Thailand & SEA', 'Australia', 'Japan', 'India'],
  },
  badges: { certified: 'Certified', level1: 'Level 1 Compliant', compliant: 'Compliant', uptime: 'Uptime SLA' },
  bottom: { rights: 'HotelStack Technologies. All rights reserved.', privacy: 'Privacy', terms: 'Terms', security: 'Security', refundPolicy: 'Refund Policy' },
};

const ar: FooterContent = {
  brand: 'منصة إدارة فنادق على مستوى المؤسسات. قم بتبسيط الحجوزات والعمليات وتجارب النزلاء بنظام موحد واحد.',
  columns: {
    platform: 'المنصة',
    solutions: 'الحلول',
    company: 'الشركة',
    legalSupport: 'القانونية والدعم',
    byPropertyType: 'حسب نوع العقار',
    compare: 'المقارنة',
    integrations: 'التكاملات',
    byCity: 'حسب المدينة',
    compliance: 'الامتثال',
  },
  links: {
    product: ['الميزات', 'الأسعار', 'برنامج الشراكة', 'لماذا HotelStack.io', 'المرونة'],
    solutions: ['إدارة الفنادق', 'نظام PMS', 'مدير القنوات', 'محرك الحجز', 'إدارة علاقات النزلاء', 'ذكاء الإيرادات'],
    company: ['من نحن', 'اتصل بنا', 'المدونة', 'سجّل فندقك'],
    support: ['برنامج الشراكة', 'BookMyRoom.io'],
    propertyTypes: ['الفنادق الصغيرة', 'فنادق البوتيك', 'المنتجعات', 'سلاسل الفنادق', 'النزل'],
    cities: ['دبي', 'بانكوك', 'لندن', 'نيويورك', 'سنغافورة', 'باريس', 'طوكيو', 'إسطنبول', 'سيدني', 'لوس أنجلوس'],
    compliance: ['الاتحاد الأوروبي والمملكة المتحدة', 'الولايات المتحدة', 'الإمارات والشرق الأوسط', 'تايلاند وجنوب شرق آسيا', 'أستراليا', 'اليابان', 'الهند'],
  },
  badges: { certified: 'معتمد', level1: 'متوافق المستوى ١', compliant: 'متوافق', uptime: 'SLA وقت التشغيل' },
  bottom: { rights: 'HotelStack Technologies. جميع الحقوق محفوظة.', privacy: 'الخصوصية', terms: 'الشروط', security: 'الأمان', refundPolicy: 'سياسة الاسترداد' },
};

const th: FooterContent = {
  brand: 'แพลตฟอร์มจัดการโรงแรมระดับองค์กร ปรับปรุงการจอง การดำเนินงาน และประสบการณ์แขกด้วยระบบเดียว',
  columns: {
    platform: 'แพลตฟอร์ม',
    solutions: 'โซลูชัน',
    company: 'บริษัท',
    legalSupport: 'กฎหมายและสนับสนุน',
    byPropertyType: 'ตามประเภทที่พัก',
    compare: 'เปรียบเทียบ',
    integrations: 'การเชื่อมต่อ',
    byCity: 'ตามเมือง',
    compliance: 'การปฏิบัติตามกฎระเบียบ',
  },
  links: {
    product: ['คุณสมบัติ', 'ราคา', 'โปรแกรมพาร์ทเนอร์', 'ทำไมต้อง HotelStack.io', 'ความยืดหยุ่น'],
    solutions: ['การจัดการโรงแรม', 'ระบบ PMS', 'ตัวจัดการช่องทาง', 'ระบบจอง', 'CRM สำหรับแขก', 'AI รายได้'],
    company: ['เกี่ยวกับเรา', 'ติดต่อ', 'บล็อก', 'ลงทะเบียนโรงแรม'],
    support: ['โปรแกรมพาร์ทเนอร์', 'BookMyRoom.io'],
    propertyTypes: ['โรงแรมขนาดเล็ก', 'โรงแรมบูทีค', 'รีสอร์ท', 'เครือโรงแรม', 'โฮสเทล'],
    cities: ['ดูไบ', 'กรุงเทพฯ', 'ลอนดอน', 'นิวยอร์ก', 'สิงคโปร์', 'ปารีส', 'โตเกียว', 'อิสตันบูล', 'ซิดนีย์', 'ลอสแอนเจลิส'],
    compliance: ['สหภาพยุโรปและสหราชอาณาจักร', 'สหรัฐอเมริกา', 'สหรัฐอาหรับเอมิเรตส์และตะวันออกกลาง', 'ไทยและเอเชียตะวันออกเฉียงใต้', 'ออสเตรเลีย', 'ญี่ปุ่น', 'อินเดีย'],
  },
  badges: { certified: 'ได้รับการรับรอง', level1: 'สอดคล้องระดับ 1', compliant: 'สอดคล้อง', uptime: 'SLA ความพร้อมใช้งาน' },
  bottom: { rights: 'HotelStack Technologies สงวนลิขสิทธิ์', privacy: 'ความเป็นส่วนตัว', terms: 'ข้อกำหนด', security: 'ความปลอดภัย', refundPolicy: 'นโยบายคืนเงิน' },
};

const es: FooterContent = {
  brand: 'Plataforma de gestión hotelera de nivel empresarial. Optimice reservas, operaciones y experiencias de huéspedes con un sistema unificado.',
  columns: {
    platform: 'Plataforma',
    solutions: 'Soluciones',
    company: 'Empresa',
    legalSupport: 'Legal y Soporte',
    byPropertyType: 'Por Tipo de Propiedad',
    compare: 'Comparar',
    integrations: 'Integraciones',
    byCity: 'Por Ciudad',
    compliance: 'Cumplimiento',
  },
  links: {
    product: ['Características', 'Precios', 'Programa de Socios', 'Por qué HotelStack.io', 'Resiliencia'],
    solutions: ['Gestión Hotelera', 'PMS Hotelero', 'Gestor de Canales', 'Motor de Reservas', 'CRM de Huéspedes', 'IA de Ingresos'],
    company: ['Sobre Nosotros', 'Contacto', 'Blog', 'Registrar Hotel'],
    support: ['Programa de Socios', 'BookMyRoom.io'],
    propertyTypes: ['Hoteles Pequeños', 'Hoteles Boutique', 'Resorts', 'Cadenas Hoteleras', 'Hostales'],
    cities: ['Dubái', 'Bangkok', 'Londres', 'Nueva York', 'Singapur', 'París', 'Tokio', 'Estambul', 'Sídney', 'Los Ángeles'],
    compliance: ['UE y Reino Unido', 'Estados Unidos', 'EAU y Oriente Medio', 'Tailandia y SEA', 'Australia', 'Japón', 'India'],
  },
  badges: { certified: 'Certificado', level1: 'Nivel 1 Compatible', compliant: 'Compatible', uptime: 'SLA de Disponibilidad' },
  bottom: { rights: 'HotelStack Technologies. Todos los derechos reservados.', privacy: 'Privacidad', terms: 'Términos', security: 'Seguridad', refundPolicy: 'Política de Reembolso' },
};

const fr: FooterContent = {
  brand: 'Plateforme de gestion hôtelière de niveau entreprise. Rationalisez les réservations, les opérations et les expériences clients avec un système unifié.',
  columns: {
    platform: 'Plateforme',
    solutions: 'Solutions',
    company: 'Entreprise',
    legalSupport: 'Juridique et Support',
    byPropertyType: 'Par Type de Propriété',
    compare: 'Comparer',
    integrations: 'Intégrations',
    byCity: 'Par Ville',
    compliance: 'Conformité',
  },
  links: {
    product: ['Fonctionnalités', 'Tarifs', 'Programme Partenaire', 'Pourquoi HotelStack.io', 'Résilience'],
    solutions: ['Gestion Hôtelière', 'PMS Hôtelier', 'Gestionnaire de Canaux', 'Moteur de Réservation', 'CRM Clients', 'IA Revenus'],
    company: ['À Propos', 'Contact', 'Blog', 'Enregistrer un Hôtel'],
    support: ['Programme Partenaire', 'BookMyRoom.io'],
    propertyTypes: ['Petits Hôtels', 'Hôtels Boutique', 'Resorts', 'Chaînes Hôtelières', 'Auberges'],
    cities: ['Dubaï', 'Bangkok', 'Londres', 'New York', 'Singapour', 'Paris', 'Tokyo', 'Istanbul', 'Sydney', 'Los Angeles'],
    compliance: ['UE et Royaume-Uni', 'États-Unis', 'EAU et Moyen-Orient', 'Thaïlande et Asie du Sud-Est', 'Australie', 'Japon', 'Inde'],
  },
  badges: { certified: 'Certifié', level1: 'Niveau 1 Conforme', compliant: 'Conforme', uptime: 'SLA de Disponibilité' },
  bottom: { rights: 'HotelStack Technologies. Tous droits réservés.', privacy: 'Confidentialité', terms: 'Conditions', security: 'Sécurité', refundPolicy: 'Politique de Remboursement' },
};

const de: FooterContent = {
  brand: 'Hotel-Management-Plattform auf Unternehmensebene. Optimieren Sie Buchungen, Betrieb und Gästeerlebnisse mit einem einheitlichen System.',
  columns: {
    platform: 'Plattform',
    solutions: 'Lösungen',
    company: 'Unternehmen',
    legalSupport: 'Recht und Support',
    byPropertyType: 'Nach Objekttyp',
    compare: 'Vergleichen',
    integrations: 'Integrationen',
    byCity: 'Nach Stadt',
    compliance: 'Compliance',
  },
  links: {
    product: ['Funktionen', 'Preise', 'Partnerprogramm', 'Warum HotelStack.io', 'Ausfallsicherheit'],
    solutions: ['Hotelmanagement', 'Hotel-PMS', 'Channel-Manager', 'Buchungsmaschine', 'Gäste-CRM', 'Revenue-KI'],
    company: ['Über Uns', 'Kontakt', 'Blog', 'Hotel Registrieren'],
    support: ['Partnerprogramm', 'BookMyRoom.io'],
    propertyTypes: ['Kleine Hotels', 'Boutique-Hotels', 'Resorts', 'Hotelketten', 'Hostels'],
    cities: ['Dubai', 'Bangkok', 'London', 'New York', 'Singapur', 'Paris', 'Tokio', 'Istanbul', 'Sydney', 'Los Angeles'],
    compliance: ['EU und UK', 'Vereinigte Staaten', 'VAE und Naher Osten', 'Thailand und Südostasien', 'Australien', 'Japan', 'Indien'],
  },
  badges: { certified: 'Zertifiziert', level1: 'Level 1 Konform', compliant: 'Konform', uptime: 'Verfügbarkeits-SLA' },
  bottom: { rights: 'HotelStack Technologies. Alle Rechte vorbehalten.', privacy: 'Datenschutz', terms: 'AGB', security: 'Sicherheit', refundPolicy: 'Rückerstattungsrichtlinie' },
};

const ja: FooterContent = {
  brand: 'エンタープライズグレードのホテル管理プラットフォーム。予約、運営、ゲスト体験を統合システムで効率化。',
  columns: {
    platform: 'プラットフォーム',
    solutions: 'ソリューション',
    company: '会社情報',
    legalSupport: '法務とサポート',
    byPropertyType: '施設タイプ別',
    compare: '比較',
    integrations: '連携',
    byCity: '都市別',
    compliance: 'コンプライアンス',
  },
  links: {
    product: ['機能', '料金', 'パートナープログラム', 'HotelStack.ioを選ぶ理由', 'レジリエンス'],
    solutions: ['ホテル管理', 'ホテルPMS', 'チャネルマネージャー', '予約エンジン', 'ゲストCRM', 'レベニューAI'],
    company: ['会社概要', 'お問い合わせ', 'ブログ', 'ホテル登録'],
    support: ['パートナープログラム', 'BookMyRoom.io'],
    propertyTypes: ['小規模ホテル', 'ブティックホテル', 'リゾート', 'ホテルチェーン', 'ホステル'],
    cities: ['ドバイ', 'バンコク', 'ロンドン', 'ニューヨーク', 'シンガポール', 'パリ', '東京', 'イスタンブール', 'シドニー', 'ロサンゼルス'],
    compliance: ['EUと英国', 'アメリカ合衆国', 'UAEと中東', 'タイと東南アジア', 'オーストラリア', '日本', 'インド'],
  },
  badges: { certified: '認証済み', level1: 'レベル1準拠', compliant: '準拠', uptime: 'アップタイムSLA' },
  bottom: { rights: 'HotelStack Technologies. 全著作権所有。', privacy: 'プライバシー', terms: '利用規約', security: 'セキュリティ', refundPolicy: '返金ポリシー' },
};

const zh: FooterContent = {
  brand: '企业级酒店管理平台。通过统一系统简化预订、运营和客人体验。',
  columns: {
    platform: '平台',
    solutions: '解决方案',
    company: '公司',
    legalSupport: '法律与支持',
    byPropertyType: '按物业类型',
    compare: '比较',
    integrations: '集成',
    byCity: '按城市',
    compliance: '合规',
  },
  links: {
    product: ['功能', '定价', '合作伙伴计划', '为什么选择HotelStack.io', '韧性'],
    solutions: ['酒店管理', '酒店PMS', '渠道管理器', '预订引擎', '客户CRM', '收入AI'],
    company: ['关于我们', '联系我们', '博客', '注册酒店'],
    support: ['合作伙伴计划', 'BookMyRoom.io'],
    propertyTypes: ['小型酒店', '精品酒店', '度假村', '连锁酒店', '青年旅舍'],
    cities: ['迪拜', '曼谷', '伦敦', '纽约', '新加坡', '巴黎', '东京', '伊斯坦布尔', '悉尼', '洛杉矶'],
    compliance: ['欧盟和英国', '美国', '阿联酋和中东', '泰国和东南亚', '澳大利亚', '日本', '印度'],
  },
  badges: { certified: '已认证', level1: '一级合规', compliant: '合规', uptime: '可用性SLA' },
  bottom: { rights: 'HotelStack Technologies. 保留所有权利。', privacy: '隐私', terms: '条款', security: '安全', refundPolicy: '退款政策' },
};

const hi: FooterContent = {
  brand: 'एंटरप्राइज़-ग्रेड होटल प्रबंधन प्लेटफॉर्म। एक एकीकृत प्रणाली से बुकिंग, संचालन और अतिथि अनुभवों को सुव्यवस्थित करें।',
  columns: {
    platform: 'प्लेटफॉर्म',
    solutions: 'समाधान',
    company: 'कंपनी',
    legalSupport: 'कानूनी और सहायता',
    byPropertyType: 'संपत्ति प्रकार के अनुसार',
    compare: 'तुलना',
    integrations: 'एकीकरण',
    byCity: 'शहर के अनुसार',
    compliance: 'अनुपालन',
  },
  links: {
    product: ['विशेषताएं', 'मूल्य', 'साझेदार कार्यक्रम', 'HotelStack.io क्यों', 'लचीलापन'],
    solutions: ['होटल प्रबंधन', 'होटल PMS', 'चैनल मैनेजर', 'बुकिंग इंजन', 'अतिथि CRM', 'रेवेन्यू AI'],
    company: ['हमारे बारे में', 'संपर्क', 'ब्लॉग', 'होटल पंजीकृत करें'],
    support: ['साझेदार कार्यक्रम', 'BookMyRoom.io'],
    propertyTypes: ['छोटे होटल', 'बुटीक होटल', 'रिसॉर्ट', 'होटल चेन', 'हॉस्टल'],
    cities: ['दुबई', 'बैंकॉक', 'लंदन', 'न्यूयॉर्क', 'सिंगापुर', 'पेरिस', 'टोक्यो', 'इस्तांबुल', 'सिडनी', 'लॉस एंजिल्स'],
    compliance: ['EU और UK', 'संयुक्त राज्य अमेरिका', 'UAE और मध्य पूर्व', 'थाईलैंड और SEA', 'ऑस्ट्रेलिया', 'जापान', 'भारत'],
  },
  badges: { certified: 'प्रमाणित', level1: 'स्तर 1 अनुपालित', compliant: 'अनुपालित', uptime: 'अपटाइम SLA' },
  bottom: { rights: 'HotelStack Technologies. सर्वाधिकार सुरक्षित।', privacy: 'गोपनीयता', terms: 'शर्तें', security: 'सुरक्षा', refundPolicy: 'धनवापसी नीति' },
};

export const FOOTER_CONTENT: LocaleContentMap<FooterContent> = { en, ar, th, es, fr, de, ja, zh, hi };
